// src/utils/theme.js — Runway-inspired pure black
import { createTheme } from '@mui/material/styles';
import { COLORS } from './color';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFFFFF',
      light: '#F5F5F5',
      dark: '#CCCCCC',
      contrastText: '#000000',
    },
    secondary: {
      main: '#999999',
      light: '#CCCCCC',
      dark: '#555555',
    },
    background: {
      default: '#000000',
      paper: '#111111',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#999999',
    },
    success: { main: COLORS.status.success },
    error: { main: COLORS.status.error },
    warning: { main: COLORS.status.warning },
    info: { main: COLORS.status.info },
  },
  typography: {
    fontFamily: ['Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'sans-serif'].join(','),
    h1: { fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: 1.1 },
    h2: { fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: 1.15 },
    h3: { fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1.2 },
    h4: { fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1.25 },
    h5: { fontWeight: 600, color: '#FFFFFF', letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, color: '#FFFFFF', letterSpacing: '-0.01em' },
    body1: { color: '#999999', lineHeight: 1.7, fontSize: '1rem' },
    body2: { color: '#999999', lineHeight: 1.6, fontSize: '0.875rem' },
    caption: { color: '#555555', fontSize: '0.75rem' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 600,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '-0.01em',
          transition: 'all 0.2s ease',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        // Primary = white button, black text — used for main CTAs
        containedPrimary: {
          backgroundColor: '#FFFFFF',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#F0F0F0',
            transform: 'translateY(-1px)',
          },
        },
        // Outlined = ghost button
        outlinedPrimary: {
          borderColor: 'rgba(255,255,255,0.15)',
          color: '#FFFFFF',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.4)',
            backgroundColor: 'rgba(255,255,255,0.04)',
          },
        },
        // Secondary outlined
        outlinedSecondary: {
          borderColor: 'rgba(255,255,255,0.1)',
          color: '#999999',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.2)',
            backgroundColor: 'rgba(255,255,255,0.03)',
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#111111',
          border: '1px solid rgba(255,255,255,0.06)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#111111',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          borderRadius: '12px',
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#0A0A0A',
            borderRadius: '8px',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
          },
          '& .MuiInputLabel-root': {
            color: '#555555',
            '&.Mui-focused': { color: '#FFFFFF' },
          },
          '& .MuiInputBase-input': { color: '#FFFFFF' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0,0,0,0.9)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          boxShadow: 'none',
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '0.75rem',
          backgroundColor: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.08)',
          color: '#999999',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(255,255,255,0.06)' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: '8px', border: '1px solid' },
        standardSuccess: {
          backgroundColor: `${COLORS.status.success}12`,
          color: COLORS.status.success,
          borderColor: `${COLORS.status.success}30`,
          '& .MuiAlert-icon': { color: COLORS.status.success },
        },
        standardError: {
          backgroundColor: `${COLORS.status.error}12`,
          color: COLORS.status.error,
          borderColor: `${COLORS.status.error}30`,
          '& .MuiAlert-icon': { color: COLORS.status.error },
        },
        standardWarning: {
          backgroundColor: `${COLORS.status.warning}12`,
          color: COLORS.status.warning,
          borderColor: `${COLORS.status.warning}30`,
          '& .MuiAlert-icon': { color: COLORS.status.warning },
        },
        standardInfo: {
          backgroundColor: `${COLORS.status.info}12`,
          color: COLORS.status.info,
          borderColor: `${COLORS.status.info}30`,
          '& .MuiAlert-icon': { color: COLORS.status.info },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A1A1A',
          border: '1px solid rgba(255,255,255,0.08)',
          color: '#FFFFFF',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.75rem',
          borderRadius: '6px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,255,255,0.06)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#FFFFFF' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          color: '#555555',
          '&.Mui-selected': { color: '#FFFFFF', fontWeight: 600 },
        },
      },
    },
  },
});

export default theme;
